import { Link } from 'react-router-dom';
import React, { useState } from 'react';
import { Alert, Button, Descriptions, Drawer, Input, Result, Select, Space, Table, Tabs, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { ApiResponse, PagedResponse } from '../../api/types';
import { formatDateTime } from '../../utils/format';

type TransactionKind = 'internal' | 'iap';
interface Transaction {
  id: string;
  user_id: string;
  username: string;
  full_name: string;
  email: string;
  created_at: string;
  updated_at: string | null;
  amount: string;
  currency: string;
  reason: string;
  feature?: string;
  reference_type: string;
  reference_id: string;
  product_id: string | null;
  product_name: string | null;
  store: string | null;
  store_transaction_id: string | null;
  transaction_type?: 'test' | 'production' | 'promo' | 'rewarded' | 'mock' | 'unknown' | null;
  status: string | null;
}

const renderReason = (record: Transaction) => (record.feature ? `${record.reason} (${record.feature})` : record.reason);
const filterDate = (value: string, nextDay = false) => {
  if (!value) return undefined;
  const date = new Date(`${value}T00:00:00Z`);
  if (nextDay) date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString();
};

const show = (value: string | null) => value || '—';
const renderTransactionType = (value?: Transaction['transaction_type']) =>
  value ? <Tag>{value.toUpperCase()}</Tag> : '—';
const renderStatus = (value: string | null) =>
  value ? (
    <Tag color={value === 'completed' ? 'green' : value === 'voided' ? 'red' : 'gold'}>{value.toUpperCase()}</Tag>
  ) : (
    '—'
  );
const renderAmount = (amount: string) => {
  if (!/^-?\d+$/.test(amount)) return show(amount);
  const value = BigInt(amount);
  return (
    <Typography.Text type={value < BigInt(0) ? 'danger' : value > BigInt(0) ? 'success' : undefined}>
      {value.toLocaleString('vi-VN')}
    </Typography.Text>
  );
};

export const TransactionsTab: React.FC<{
  kind: TransactionKind;
  userID?: string;
}> = ({ kind, userID }) => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [reason, setReason] = useState<string>();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const invalidRange = Boolean(from && to && from > to);
  const reasons = useQuery({
    queryKey: ['admin-transaction-reasons'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<string[]>>('/api/v1/admin/transactions/internal/reasons');
      return response.data.data;
    },
    enabled: kind === 'internal',
  });
  const [selectedID, setSelectedID] = useState<string>();
  const list = useQuery({
    queryKey: ['admin-transactions', kind, userID, page, perPage, search, reason, from, to],
    queryFn: async () => {
      const response = await apiClient.get<PagedResponse<Transaction>>(`/api/v1/admin/transactions/${kind}`, {
        params: {
          page,
          per_page: perPage,
          reason,
          date_from: filterDate(from),
          date_to: filterDate(to, true),
          ...(search ? { search } : {}),
          ...(userID ? { user_id: userID } : {}),
        },
      });
      return response.data;
    },
    placeholderData: keepPreviousData,
    enabled: !invalidRange,
  });
  const detail = useQuery({
    queryKey: ['admin-transaction', kind, selectedID],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Transaction>>(
        `/api/v1/admin/transactions/${kind}/${encodeURIComponent(selectedID!)}`
      );
      return response.data.data;
    },
    enabled: Boolean(selectedID),
  });

  const columns: ColumnsType<Transaction> = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    ...(!userID
      ? [
          {
            title: 'User ID',
            dataIndex: 'user_id',
            key: 'user_id',
            render: (value: string) => <Link to={`/users/${value}`}>{value}</Link>,
          },
          {
            title: 'Name',
            dataIndex: 'full_name',
            key: 'full_name',
            render: (value: string, row: Transaction) =>
              value ? <Link to={`/users/${row.user_id}`}>{value}</Link> : '—',
          },
        ]
      : []),
    ...(kind === 'internal'
      ? [
          {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: renderAmount,
          },
          { title: 'Currency', dataIndex: 'currency', key: 'currency' },
          {
            title: 'Reason',
            key: 'reason',
            render: (_: unknown, row: Transaction) => renderReason(row),
          },
        ]
      : [
          {
            title: 'Product',
            dataIndex: 'product_name',
            key: 'product_name',
            render: show,
          },
          { title: 'Store', dataIndex: 'store', key: 'store', render: show },
          {
            title: 'Store transaction ID',
            dataIndex: 'store_transaction_id',
            key: 'store_transaction_id',
            render: show,
          },
          {
            title: 'Transaction type',
            dataIndex: 'transaction_type',
            key: 'transaction_type',
            render: renderTransactionType,
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: renderStatus,
          },
        ]),
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: formatDateTime,
    },
    {
      title: '',
      key: 'view',
      render: (_, row) => (
        <Button type="link" onClick={() => setSelectedID(row.id)}>
          View
        </Button>
      ),
    },
  ];
  const record = detail.data;
  const detailFields = record
    ? [
        { key: 'id', label: 'Transaction ID', children: record.id },
        {
          key: 'user',
          label: 'User ID',
          children: <Link to={`/users/${record.user_id}`}>{record.user_id}</Link>,
        },
        ...(kind === 'iap'
          ? [
              {
                key: 'username',
                label: 'Username',
                children: <Link to={`/users/${record.user_id}`}>{record.username}</Link>,
              },
            ]
          : []),
        {
          key: 'name',
          label: 'Full name',
          children: record.full_name ? <Link to={`/users/${record.user_id}`}>{record.full_name}</Link> : '—',
        },
        ...(kind === 'iap' ? [{ key: 'email', label: 'Email', children: show(record.email) }] : []),
        {
          key: 'created',
          label: 'Created',
          children: formatDateTime(record.created_at),
        },
        ...(kind === 'internal'
          ? [
              {
                key: 'amount',
                label: 'Amount',
                children: renderAmount(record.amount),
              },
              { key: 'currency', label: 'Currency', children: record.currency },
              {
                key: 'reason',
                label: 'Reason',
                children: renderReason(record),
              },
              {
                key: 'feature',
                label: 'Feature JSON',
                children: (
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(record.feature ? { feature: record.feature } : {}, null, 2)}
                  </pre>
                ),
              },
              {
                key: 'reference-type',
                label: 'Reference type',
                children: show(record.reference_type),
              },
              {
                key: 'reference-id',
                label: 'Reference ID',
                children: show(record.reference_id),
              },
              {
                key: 'updated',
                label: 'Updated',
                children: formatDateTime(record.updated_at),
              },
            ]
          : [
              {
                key: 'product-id',
                label: 'Product ID',
                children: show(record.product_id),
              },
              {
                key: 'product-name',
                label: 'Product name',
                children: show(record.product_name),
              },
              { key: 'store', label: 'Store', children: show(record.store) },
              {
                key: 'store-id',
                label: 'Store transaction ID',
                children: show(record.store_transaction_id),
              },
              {
                key: 'transaction-type',
                label: 'Transaction type',
                children: renderTransactionType(record.transaction_type),
              },
              {
                key: 'status',
                label: 'Status',
                children: renderStatus(record.status),
              },
            ]),
      ]
    : [];

  return (
    <>
      <Space className="mb-4" wrap align="end">
        <Input.Search
          aria-label="Search transactions"
          placeholder={kind === 'internal' ? 'Search ID, name, reason, or feature' : 'Search ID, name, or email'}
          maxLength={255}
          allowClear
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
            if (!event.target.value) {
              setSearch('');
              setPage(1);
            }
          }}
          onSearch={(value) => {
            setSearch(value.trim());
            setPage(1);
          }}
          style={{ width: 320 }}
        />
        {kind === 'internal' && (
          <Select
            aria-label="Filter by reason"
            placeholder="All reasons"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ minWidth: 240 }}
            value={reason}
            loading={reasons.isLoading}
            options={(reasons.data ?? ['daily_support']).filter(Boolean).map((value) => ({
              value,
              label: value === 'daily_support' ? 'Daily Support' : value,
            }))}
            onChange={(value) => {
              setReason(value);
              setPage(1);
            }}
          />
        )}
        <label>
          From (UTC)
          <Input
            type="date"
            aria-label="Transactions from date (UTC)"
            value={from}
            max={to || undefined}
            onChange={(event) => {
              setFrom(event.target.value);
              setPage(1);
            }}
          />
        </label>
        <label>
          To (UTC, inclusive)
          <Input
            type="date"
            aria-label="Transactions to date (UTC, inclusive)"
            value={to}
            min={from || undefined}
            onChange={(event) => {
              setTo(event.target.value);
              setPage(1);
            }}
          />
        </label>
        <Button
          onClick={() => {
            setSearchInput('');
            setSearch('');
            setReason(undefined);
            setFrom('');
            setTo('');
            setPage(1);
          }}
        >
          Clear filters
        </Button>
        <Button
          icon={<ReloadOutlined />}
          loading={list.isFetching}
          disabled={invalidRange}
          onClick={() => list.refetch()}
        >
          Refresh
        </Button>
      </Space>
      {invalidRange && <Alert type="error" message="From date must be on or before To date." className="mb-4" />}
      {kind === 'internal' && reasons.error && (
        <Alert
          type="error"
          message="Failed to load reason filters"
          action={<Button onClick={() => reasons.refetch()}>Retry</Button>}
          className="mb-4"
        />
      )}
      {list.error ? (
        <Result
          status="error"
          title="Failed to load transactions"
          subTitle={list.error instanceof Error ? list.error.message : 'An unexpected error occurred'}
          extra={<Button onClick={() => list.refetch()}>Retry</Button>}
        />
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={invalidRange ? [] : (list.data?.data ?? [])}
          loading={list.isLoading}
          scroll={{ x: 1100 }}
          pagination={{
            current: page,
            pageSize: perPage,
            total: invalidRange ? 0 : (list.data?.meta.total ?? 0),
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            disabled: list.isPlaceholderData,
            showTotal: (total) => `${total} transactions`,
            onChange: (nextPage, nextSize) => {
              setPage(nextSize === perPage ? nextPage : 1);
              setPerPage(nextSize);
            },
          }}
        />
      )}
      <Drawer
        title={kind === 'internal' ? 'Internal transaction detail' : 'IAP payment detail'}
        width={720}
        open={Boolean(selectedID)}
        onClose={() => setSelectedID(undefined)}
        destroyOnClose
      >
        {detail.error ? (
          <Result
            status="error"
            title="Failed to load transaction"
            subTitle={detail.error instanceof Error ? detail.error.message : 'An unexpected error occurred'}
            extra={<Button onClick={() => detail.refetch()}>Retry</Button>}
          />
        ) : detail.isLoading ? (
          'Loading transaction…'
        ) : (
          <Descriptions bordered column={1} items={detailFields} />
        )}
      </Drawer>
    </>
  );
};

export const TransactionsPage: React.FC = () => (
  <Tabs
    defaultActiveKey="internal"
    items={[
      {
        key: 'internal',
        label: 'Internal transaction',
        children: <TransactionsTab kind="internal" />,
      },
      {
        key: 'iap',
        label: 'IAP payment',
        children: <TransactionsTab kind="iap" />,
      },
    ]}
  />
);
